import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Loader2, KeyRound } from 'lucide-react';
import { NeoButton, NeoInput } from '../NexusUI';

interface MfaChallengeProps {
  onVerified: () => void;
  onCancel: () => void;
}

export function MfaChallenge({ onVerified, onCancel }: MfaChallengeProps) {
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const { data: factors, error: factorsError } = await supabase!.auth.mfa.listFactors();
      if (factorsError) throw factorsError;
      
      const totpFactor = factors.totp[0];
      if (!totpFactor) throw new Error('No TOTP factors found!');
      
      const challenge = await supabase!.auth.mfa.challenge({ factorId: totpFactor.id });
      if (challenge.error) throw challenge.error;
      
      const verify = await supabase!.auth.mfa.verify({
        factorId: totpFactor.id,
        challengeId: challenge.data.id,
        code: verifyCode
      });
      
      if (verify.error) throw verify.error;
      
      onVerified();
      
    } catch (err: any) {
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="mb-6 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 mb-4">
          <KeyRound className="h-6 w-6 text-primary" />
        </div>
        <h2 className="text-xl font-bold tracking-tight">Two-Factor Authentication</h2>
        <p className="text-sm text-muted-foreground mt-2">
          Enter the 6-digit code from your authenticator app to continue.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-500 text-center font-medium">
          {error}
        </div>
      )}

      <form onSubmit={handleVerify} className="space-y-4">
        <div className="space-y-2">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground ml-2">
            Verification Code
          </label>
          <NeoInput
            type="text"
            placeholder="000000"
            maxLength={6}
            value={verifyCode}
            onChange={(e: any) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
            required
            autoFocus
          />
        </div>
        
        <NeoButton
          type="submit"
          variant="primary"
          disabled={loading || verifyCode.length < 6}
          className="w-full flex justify-center items-center gap-2"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Verify Code
        </NeoButton>
        
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel and sign out
          </button>
        </div>
      </form>
    </div>
  );
}
